import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, CloseButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { SHXButton } from '@soulhx/ui-common';
import { SHXEditor } from './SHXEDitor.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  addDisplayedHT,
  getDisplayedHelpTexts,
  getForceHideHelpText,
  getShowAllHelpText,
  useUserSetting,
} from '@soulhx/client-store';
import { type BootstrapVariant } from '@soulhx/fs-common';
import { useSearchParams } from 'react-router-dom';

interface IShxHelpText {
  /**
   * Main page within which this sits
   */
  pageName: string;

  /**
   * Specific Help Text for the page
   */
  textName: string;

  /**
   * Additional CSS/Bootstrap classes
   */
  className?: string;

  /**
   * (for inline) indicates that the item should be shown initially collapsed
   */
  collapsed?: boolean;

  /**
   * Size of the icon for this HT (for inline)
   */
  size?: number;

  /**
   * Should be shown as an inline item, as opposed to taking up real estate
   */
  inline?: boolean;

  /**
   * Indicates that this HT should be shown even if the maximum has been exceeded
   */
  firstLevel?: boolean;

  /**
   * Bootstrap variant
   */
  variant?: BootstrapVariant;
}
export const ShxHelpText = ({
  pageName,
  textName,
  className,
  collapsed = false,
  size = 25,
  inline = false,
  firstLevel = false,
  variant = 'info',
}: IShxHelpText) => {
  const {
    settingValue: showSetting,
    update: updateVal,
    isLoading: showSettingLoading,
  } = useUserSetting<boolean>(`helpText.${pageName}.${textName}`);
  const { settingValue: maxAllowedString, isLoading: maxAllowedLoading } =
    useUserSetting<string>('generalNumHelpTexts');
  const { t } = useTranslation(['helpText']);
  const [showHiddenAlert, setShowHiddenAlert] = useState(!collapsed);
  const displayedHTs = useSelector(getDisplayedHelpTexts);
  const showAllHelpText = useSelector(getShowAllHelpText);
  const forceHide = useSelector(getForceHideHelpText);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const isPopout = useMemo(() => searchParams.get('popout') === 'true', [searchParams]);

  const MAX_ALLOWED_ON_SCREEN = useMemo(() => parseInt(maxAllowedString) || 3, [maxAllowedString]);

  const htName = `helpText:${pageName}.${textName}`;

  const isDisplayed = useMemo(() => displayedHTs.includes(htName), [displayedHTs, htName]);

  const eligible = useMemo(
    () =>
      !isPopout &&
      !forceHide &&
      showSetting &&
      (firstLevel || showAllHelpText || displayedHTs.length < MAX_ALLOWED_ON_SCREEN),
    [forceHide, showSetting, firstLevel, showAllHelpText, displayedHTs, MAX_ALLOWED_ON_SCREEN, isPopout]
  );

  useEffect(() => {
    if (showSettingLoading || maxAllowedLoading) return;

    if (eligible && !isDisplayed) {
      dispatch(addDisplayedHT({ name: htName, max: MAX_ALLOWED_ON_SCREEN, firstLevel }));
    }
  }, [
    eligible,
    isDisplayed,
    htName,
    dispatch,
    firstLevel,
    MAX_ALLOWED_ON_SCREEN,
    showSettingLoading,
    maxAllowedLoading,
  ]);

  const showThisHt = showAllHelpText || (!forceHide && showSetting && isDisplayed);

  if (!showThisHt) return null;

  const theAlert = inline ? (
    <Badge bg="" className={`bg-${variant}-subtle text-${variant}-emphasis`}>
      <span className="float-end">
        <CloseButton className="p-1" onClick={() => updateVal(false)} />
      </span>
      <SHXEditor readOnly className="me-3" initialMarkdown={t(`helpText:${pageName}.${textName}`)} />
    </Badge>
  ) : (
    <Alert variant={variant} className={`p-1 pe-3 ${className || ''}`} dismissible onClose={() => updateVal(false)}>
      <SHXEditor readOnly initialMarkdown={t(`helpText:${pageName}.${textName}`)} disableMaxHeight disableMaxWidth />
    </Alert>
  );

  if (!collapsed) return theAlert;

  return (
    <>
      {showHiddenAlert && theAlert}
      <SHXButton variant={`outline-${variant}`} onClick={() => setShowHiddenAlert((prev) => !prev)}>
        <InfoCircleFill height={size} width={size} />
      </SHXButton>
    </>
  );
};
